import { useState, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { LICENSE_TYPES, LICENSE_TYPE_MAP, resolveLicenseType, readUrlContext } from './lib/licenseTypes';
import StateLookupPanel from './StateLookupPanel';
import ProfileView, { ProfileChip, ProfilePrompt } from './ProfileView';

// ============================================
// CONFIGURATION
// ============================================

const BLOCKED_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'protonmail.com', 'proton.me', 'icloud.com', 'aol.com', 'mail.com'
];

// Superuser status is decided on the server (api/_auth.js) and returned by /api/me.

// One login for the Writr suite: sign-in and sign-up happen at the producer dashboard,
// which opens Licensr inside its shell at /tools/licensr and signs the user in here
// with a one-time handoff (/api/auth?action=sso). ?embed=1 (UpWritr) is unaffected.
const DASHBOARD_URL = 'https://app.upfrontrisk.io';
const dashboardUrl = (register = false, type = null) => {
  const q = new URLSearchParams();
  if (register) q.set('mode', 'register');
  if (type) q.set('type', type);
  const qs = q.toString();
  return `${DASHBOARD_URL}/tools/licensr${qs ? `?${qs}` : ''}`;
};
// True when Licensr is running inside the dashboard (or was handed off from it this visit).
const IN_SHELL = window.self !== window.top;
const goTop = (url) => { try { window.top.location.href = url; } catch { window.location.href = url; } };
const notifyDashboard = (type) => {
  if (IN_SHELL) window.parent.postMessage({ type }, DASHBOARD_URL);
};

// MLCC's own site serves business/entity names HTML-entity-encoded (e.g. "&amp;"
// for "&"), and the Apify actor carries that through verbatim. Decode the common
// entities before display so "THE POUND BAR &amp; GRILL" renders as "THE POUND
// BAR & GRILL" everywhere — report view, candidate picker, and PDF export alike.
const HTML_ENTITY_MAP = {
  '&amp;': '&', '&AMP;': '&',
  '&lt;': '<', '&gt;': '>',
  '&quot;': '"', '&#39;': "'", '&apos;': "'",
  '&nbsp;': ' ',
};
function decodeHtmlEntities(str) {
  if (typeof str !== 'string' || !str.includes('&')) return str;
  return str.replace(/&amp;|&AMP;|&lt;|&gt;|&quot;|&#39;|&apos;|&nbsp;|&#\d+;/g, (m) => {
    if (HTML_ENTITY_MAP[m]) return HTML_ENTITY_MAP[m];
    const num = m.match(/^&#(\d+);$/);
    return num ? String.fromCharCode(parseInt(num[1], 10)) : m;
  });
}

// ============================================
// PIPELINE STAGES CONFIGURATION
// ============================================

// Licensr returns OFFICIAL GOVERNMENT-OF-RECORD facts only. Web / Google
// Places / social / review enrichment is owned by UpWritr and is intentionally
// NOT part of this pipeline.
const PIPELINE_STAGES = [
  {
    id: 'mlcc',
    name: 'MLCC License Database',
    description: 'License verification & status (weekly cache)',
    icon: '📋'
  },
  {
    id: 'official',
    name: 'Live Official Record',
    description: 'Violations, insurance carrier, spirit sales & entity — pulled live from MLCC',
    icon: '🏛️'
  },
  {
    id: 'risk',
    name: 'Risk Analysis',
    description: 'Underwriting risk score from official record',
    icon: '📊'
  }
];

// Surfaced on every result. This source does not index fully revoked or
// long-expired licenses — underwriters must know the boundary of the search.
const COVERAGE_SCOPE_NOTE =
  'This search covers active, escrow, conditional, and suspended licenses only; ' +
  'fully revoked or expired licenses are not indexed here.';

// ============================================
// RISK ANALYSIS ENGINE
// ============================================

// Risk is computed strictly from OFFICIAL RECORD signals: license status,
// violation history (count + open dispositions), liquor-liability insurance on
// file, and license type. No web / operational enrichment factors — those are
// scored downstream by UpWritr.
function calculateRiskAnalysis(license, official, violations) {
  let riskScore = 30;
  const factors = [];
  const flags = [];

  const status = (license?.status || official?.status || '').toLowerCase();
  const insurance = official?.insurance || [];
  const hasActiveInsurance = insurance.some(
    i => (i.status || '').toLowerCase() === 'active'
  );

  // ---- License status ----
  if (status.includes('suspend')) {
    riskScore += 30;
    factors.push({ factor: "Suspended License", impact: "severe", score_impact: 30, description: "License is currently suspended", source: "🏛️ Official Record" });
    flags.push({ flag_type: "compliance", severity: "critical", description: "License suspended", recommendation: "Do not bind until reinstated" });
  } else if (status.includes('escrow')) {
    riskScore += 15;
    factors.push({ factor: "Escrowed License", impact: "negative", score_impact: 15, description: "License is in escrow (not actively operating)", source: "🏛️ Official Record" });
    flags.push({ flag_type: "compliance", severity: "warning", description: "License in escrow", recommendation: "Confirm operating intent before binding" });
  } else if (status.includes('conditional')) {
    riskScore += 12;
    factors.push({ factor: "Conditional License", impact: "negative", score_impact: 12, description: "License held under conditional status", source: "🏛️ Official Record" });
  } else if (status.includes('active')) {
    riskScore -= 5;
    factors.push({ factor: "Active License", impact: "positive", score_impact: -5, description: "License is active and in good status", source: "🏛️ Official Record" });
  }

  // ---- Violations ----
  const violationList = violations || [];
  const violationCount = violationList.length;
  const openViolations = violationList.filter(
    v => v.status && !/(closed|resolved|paid|dismissed)/i.test(v.status)
  ).length;

  if (violationCount === 0) {
    riskScore -= 10;
    factors.push({ factor: "Clean Record", impact: "positive", score_impact: -10, description: "No violations on the official record", source: "🏛️ Official Record" });
  } else if (violationCount <= 2) {
    riskScore += 15;
    factors.push({ factor: "Violation History", impact: "negative", score_impact: 15, description: `${violationCount} violation(s) on record`, source: "🏛️ Official Record" });
  } else {
    riskScore += 30;
    factors.push({ factor: "Extensive Violations", impact: "severe", score_impact: 30, description: `${violationCount} violations — pattern of non-compliance`, source: "🏛️ Official Record" });
    flags.push({ flag_type: "violation_history", severity: "critical", description: "Pattern of violations", recommendation: "Refer to underwriting" });
  }
  if (openViolations > 0) {
    riskScore += 10;
    factors.push({ factor: "Open Violations", impact: "negative", score_impact: 10, description: `${openViolations} violation(s) not yet closed`, source: "🏛️ Official Record" });
    flags.push({ flag_type: "compliance", severity: "warning", description: "Unresolved violation(s) on record", recommendation: "Confirm disposition before binding" });
  }

  // ---- Liquor-liability insurance on file ----
  if (insurance.length === 0) {
    riskScore += 8;
    factors.push({ factor: "No Insurance on File", impact: "negative", score_impact: 8, description: "No liquor-liability carrier listed on the MLCC record", source: "🏛️ Official Record" });
    flags.push({ flag_type: "coverage", severity: "warning", description: "No liquor-liability carrier on file", recommendation: "Verify current coverage directly" });
  } else if (hasActiveInsurance) {
    riskScore -= 5;
    factors.push({ factor: "Active Liquor-Liability Coverage", impact: "positive", score_impact: -5, description: `Carrier on file: ${insurance.find(i => (i.status || '').toLowerCase() === 'active')?.carrier || 'listed'}`, source: "🏛️ Official Record" });
  }

  // ---- License type ----
  const licenseType = license?.license_type?.description || license?.license_type || official?.license_type || '';
  if (licenseType.toLowerCase().includes('class c') || licenseType.toLowerCase().includes('spirits')) {
    riskScore += 5;
    factors.push({ factor: "Full Liquor License", impact: "neutral", score_impact: 5, description: "Spirits service increases exposure", source: "🏛️ Official Record" });
  }

  // Clamp score
  riskScore = Math.max(1, Math.min(100, riskScore));

  let tier;
  if (riskScore <= 25) tier = "low";
  else if (riskScore <= 45) tier = "moderate";
  else if (riskScore <= 65) tier = "elevated";
  else if (riskScore <= 85) tier = "high";
  else tier = "severe";

  return {
    overall_risk_score: riskScore,
    risk_tier: tier,
    risk_factors: factors,
    underwriting_flags: flags
  };
}

// ============================================
// MAIN APP COMPONENT
// ============================================

export default function LicensrApp() {
  const [view, setView] = useState('opening');
  const [user, setUser] = useState(null);
  const [lookupCount, setLookupCount] = useState(0);
  const [quota, setQuota] = useState({ remaining: null, monthlyLimit: null, hasEntitlement: false, loaded: false });
  const [showLoginMode, setShowLoginMode] = useState(false);
  const [me, setMe] = useState(null);            // full /api/me state (profile, completion, pulls)
  const [showProfile, setShowProfile] = useState(false);
  const [promptHidden, setPromptHidden] = useState(false); // "Maybe later" for this visit
  const [notice, setNotice] = useState('');
  const [loginNotice, setLoginNotice] = useState('');
  const [registerNotice, setRegisterNotice] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [linkedinEnabled, setLinkedinEnabled] = useState(false);
  
  // Pipeline state
  const [pipelineActive, setPipelineActive] = useState(false);
  const [pipelineStages, setPipelineStages] = useState({});
  const [pipelineResults, setPipelineResults] = useState({});
  const [pipelineInputs, setPipelineInputs] = useState({});
  
  // Report state
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState('overview');

  // License-type selection (drives the carousel + headless UpWritr embed)
  const [selectedType, setSelectedType] = useState(null); // license-type config object
  const [embed, setEmbed] = useState(false);

  useEffect(() => {
    // Read UpWritr / deep-link context: ?type=cannabis & ?embed=1
    const ctx = readUrlContext();
    if (ctx.embed) setEmbed(true);
    const presetType = ctx.type ? LICENSE_TYPE_MAP[ctx.type] : null;
    if (presetType) setSelectedType(presetType);

    // One front door, including stale Licensr magic links. Do not rely on a
    // persistent "shell" flag: it previously stranded direct visitors here.
    if (!IN_SHELL && !ctx.embed) {
      window.location.replace(dashboardUrl(false, ctx.type || null));
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const failedHandoff = params.get('auth') === 'sso_failed';
    for (const key of ['auth', 'shell', 'first', 'last']) params.delete(key);
    window.history.replaceState({}, '', window.location.pathname + (params.toString() ? `?${params}` : ''));

    (async () => {
      // An explicitly failed handoff must not fall back to a previous user's
      // still-valid Licensr cookie.
      if (failedHandoff && !ctx.embed) {
        setView('shell_error');
        notifyDashboard('licensr:auth-error');
        return;
      }
      const state = await loadMe();
      if (state) {
        setView(presetType && presetType.slug === 'liquor' ? 'lookup' : 'select');
        if (!ctx.embed) notifyDashboard('licensr:ready');
        return;
      }
      if (IN_SHELL && !ctx.embed) {
        setView('shell_error');
        notifyDashboard('licensr:auth-error');
        return;
      }
      // Preserve the separate UpWritr integration; its server-side embed key
      // remains the authority for protected API requests.
      setView(ctx.embed && presetType ? 'select' : 'landing');
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loads the signed-in user from the session cookie. Returns the state or null.
  const loadMe = async () => {
    try {
      const res = await fetch('/api/me', { credentials: 'same-origin' });
      if (!res.ok) {
        setMe(null); setUser(null);
        if (res.status === 401 && IN_SHELL && !readUrlContext().embed) notifyDashboard('licensr:auth-error');
        return null;
      }
      const state = await res.json();
      applyMe(state);
      return state;
    } catch {
      return null;
    }
  };

  const applyMe = (state) => {
    setMe(state);
    setUser(state.user);
    const q = state.quota || {};
    setQuota({
      remaining: q.remaining ?? null,
      monthlyLimit: q.monthlyLimit ?? null,
      hasEntitlement: !!q.hasEntitlement,
      loaded: true,
      degraded: !!q.degraded,
    });
  };

  const handleRegister = async (userData) => {
    const emailLower = userData.email.toLowerCase().trim();
    const domain = emailLower.split('@')[1];
    if (BLOCKED_DOMAINS.includes(domain)) {
      return { success: false, error: 'Please use your work email. You can add a personal email to your profile later.' };
    }
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: userData.firstName,
          lastName: userData.lastName,
          agencyState: userData.agencyState || 'MI',
          email: emailLower,
        }),
      });
      let data;
      try { data = await response.json(); } catch { return { success: false, error: 'Server error. Please try again.' }; }
      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Registration failed. Please try again.' };
      }
      if (data.existing) {
        return { success: false, linkSent: true, email: emailLower };
      }
      const state = await loadMe();
      if (!state) return { success: false, error: 'Account created, but we could not start your session. Please sign in.' };
      setNotice(`You're in. We sent a welcome email to ${emailLower}.`);
      setView(selectedType && selectedType.slug === 'liquor' ? 'lookup' : 'select');
      return { success: true };
    } catch {
      return { success: false, error: 'Network error. Please check your connection.' };
    }
  };

  const handleLoginRequest = async (email) => {
    try {
      const r = await fetch('/api/auth?action=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      return d;
    } catch {
      return { success: false, error: 'Network error. Please check your connection.' };
    }
  };

  const dismissPrompt = async () => {
    setPromptHidden(true);
    try {
      const r = await fetch('/api/me', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dismissPrompt: true }) });
      if (r.ok) applyMe(await r.json());
    } catch { /* non-blocking */ }
  };

  const handleLogout = () => {
    fetch('/api/auth?action=logout', { method: 'POST' }).catch(() => {});
    localStorage.removeItem('licensr_user');
    setMe(null);
    setUser(null);
    setQuota({ remaining: null, monthlyLimit: null, hasEntitlement: false, loaded: false });
    setView('landing');
    setPipelineActive(false);
    setPipelineStages({});
    setPipelineResults({});
    setSelectedLicense(null);
    setSelectedType(null);
  };

  // Quota, pull count and profile completion all come from /api/me.
  const refreshQuota = async () => { await loadMe(); };

  // Atomically checks + records ONE metered action against the hub. Call this
  // right before running a real lookup OR opening a deep-link verification page.
  // Returns true if allowed (and refreshes the displayed quota), false if blocked.
  const checkAndConsumeQuota = async (scope, eventType) => {
    if (user?.isSuperuser) return true;
    try {
      const res = await fetch('/api/usage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, eventType }),
      });
      const data = await res.json();
      setQuota((prev) => ({
        ...prev,
        remaining: data.remaining,
        monthlyLimit: data.monthlyLimit ?? prev.monthlyLimit,
        degraded: !!data.degraded,
        loaded: true,
      }));
      return data.allowed !== false;
    } catch {
      // Network failure talking to our OWN /api/usage — fail open so a transient
      // blip never blocks a producer; the hub-side RPC already fails open too.
      return true;
    }
  };

  const resetPipeline = () => {
    setPipelineActive(false);
    setPipelineStages({});
    setPipelineResults({});
    setPipelineInputs({});
    setSelectedLicense(null);
    // Return to the license-type picker so producers can pick another type.
    setView(user ? 'select' : 'landing');
  };

  // Called when a license type is chosen (by the user or preset upstream).
  const handleSelectType = (typeConfig) => {
    setSelectedType(typeConfig);
    const resolved = resolveLicenseType(typeConfig);
    if (resolved.action === 'run_engine') {
      // Liquor -> existing intelligence pipeline
      setView('lookup');
    }
    // verify / cached types render their result card within SelectView itself.
  };

  return (
    <div style={styles.container}>
      <div style={styles.bgPattern}></div>
      
      <header style={styles.header}>
        <div style={styles.logoContainer} onClick={() => user ? resetPipeline() : setView('landing')}>
          <div style={styles.logoIcon}>🍸</div>
          <div style={styles.logoText}>
            <span style={styles.logoMain}>Licens<span style={styles.logoAccent}>r</span></span>
            <span style={styles.logoSub}>by Writr Suite</span>
          </div>
        </div>
        
        {user && (
          <div style={styles.headerRight}>
            <div style={styles.stateBadge}>🏛️ Michigan</div>
            {user.isSuperuser ? (
              <div style={styles.superuserBadge}>⭐ Superuser</div>
            ) : (
              <div style={styles.lookupCounter}>
                <span style={styles.counterLabel}>Free Lookups / Month</span>
                <span style={styles.counterValue}>
                  {quota.loaded
                    ? (quota.hasEntitlement ? `${quota.remaining ?? 0}/${quota.monthlyLimit ?? '—'}` : 'No plan')
                    : '…'}
                </span>
              </div>
            )}
            {IN_SHELL && !readUrlContext().embed
              ? <button style={styles.logoutBtn} onClick={() => notifyDashboard('licensr:open-profile')}>Profile</button>
              : <ProfileChip me={me} onClick={() => setShowProfile(true)} />}
            {!IN_SHELL && <button onClick={handleLogout} style={styles.logoutBtn}>Sign Out</button>}
          </div>
        )}
      </header>

      <main style={styles.main}>
        {notice && (
          <div style={styles.noticeBar}>
            <span>{notice}</span>
            <button onClick={() => setNotice('')} style={styles.noticeClose} aria-label="Dismiss">×</button>
          </div>
        )}
        {(!IN_SHELL || readUrlContext().embed) && user && me?.showProfilePrompt && !promptHidden && !showProfile && ['select', 'report'].includes(view) && (
          <ProfilePrompt me={me} onOpen={() => { setPromptHidden(true); setShowProfile(true); }} onLater={dismissPrompt} />
        )}
        {user && showProfile && me && (
          <ProfileView me={me} states={US_STATES} linkedinEnabled={linkedinEnabled} onClose={() => setShowProfile(false)} onSaved={applyMe} />
        )}
        {view === 'landing' && (
          <LandingView onGetStarted={() => goTop(dashboardUrl(true, selectedType?.slug))} onSignIn={() => goTop(dashboardUrl(false, selectedType?.slug))} />
        )}
        {view === 'opening' && <p role="status" style={{ color: '#94a3b8', textAlign: 'center', padding: 48 }}>Opening Licensr from your Producer Dashboard…</p>}
        {view === 'shell_error' && (
          <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 24px' }}>
            <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 28, marginBottom: 12 }}>Connecting to your dashboard</h2>
            <p style={{ color: '#94a3b8', marginBottom: 24 }}>Use the dashboard's retry button if this connection does not complete. No separate Licensr login is needed.</p>
          </div>
        )}
        {view === 'select' && (
          <SelectView
            selectedType={selectedType}
            embed={embed}
            onSelectType={handleSelectType}
            onClear={() => setSelectedType(null)}
            quota={quota}
            checkAndConsumeQuota={checkAndConsumeQuota}
            user={user}
            onQuotaChange={() => refreshQuota(user)}
          />
        )}
        {view === 'lookup' && !pipelineActive && (
          <LookupView 
            licenseType={selectedType}
            onBack={() => { setSelectedType(null); setView('select'); }}
            onStartPipeline={async (inputs) => {
              // Liquor's quota is consumed server-side inside /api/violations itself
              // (the actual metered engine call) — no client-side pre-check needed here,
              // avoiding double-counting. PipelineView surfaces a quota_exceeded response.
              setPipelineInputs(inputs);
              setPipelineActive(true);
              setView('pipeline');
              return true;
            }}
            quota={quota}
            isSuperuser={user?.isSuperuser}
          />
        )}
        {view === 'pipeline' && pipelineActive && (
          <PipelineView
            inputs={pipelineInputs}
            user={user}
            stages={pipelineStages}
            setStages={setPipelineStages}
            results={pipelineResults}
            setResults={setPipelineResults}
            onQuotaExceeded={() => refreshQuota(user)}
            onComplete={(finalResults) => {
              if (!user?.isSuperuser) {
                const newCount = lookupCount + 1;
                setLookupCount(newCount);
                localStorage.setItem('licensr_count', newCount.toString());
              }
              // Record usage for funnel/metrics (fire-and-forget; never blocks the report).
              try {
                fetch('/api/track-usage', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    licenseNumber:
                      finalResults.official?.license_number ||
                      finalResults.license?.license_number ||
                      pipelineInputs.licenseNumber ||
                      null,
                    businessName:
                      finalResults.official?.licensee_name ||
                      finalResults.license?.business_name ||
                      pipelineInputs.businessName ||
                      pipelineInputs.dba ||
                      null,
                    city: pipelineInputs.city || null,
                    resultsCount: finalResults.no_record ? 0 : 1,
                    riskScore: finalResults.risk_analysis?.overall_risk_score ?? null,
                    searchQuery: pipelineInputs,
                  }),
                }).catch(() => {});
              } catch { /* metrics must never break the report */ }
              refreshQuota(); // pulls + quota -> may raise the soft profile prompt
              setSelectedLicense(finalResults);
              setView('report');
            }}
            onCancel={resetPipeline}
          />
        )}
        {view === 'report' && selectedLicense && (
          <ReportView
            data={selectedLicense}
            activeTab={activeReportTab}
            setActiveTab={setActiveReportTab}
            onNewSearch={resetPipeline}
          />
        )}
      </main>

      <footer style={styles.footer}>
        <p>© {new Date().getFullYear()} Writr Suite · Licensr Michigan</p>
      </footer>
    </div>
  );
}

// ============================================
// LANDING VIEW
// ============================================

function LandingView({ onGetStarted, onSignIn }) {
  return (
    <div style={styles.landingContainer}>
      <div style={styles.heroContent}>
        <div style={styles.badge}>🔎 Michigan License Intelligence</div>
        <h1 style={styles.heroTitle}>
          Complete License<br />
          <span style={styles.heroAccent}>Verification for Insurance Professionals</span>
        </h1>
        <p style={styles.heroSubtitle}>
          Official license status, violation history, and entity intelligence for
          Michigan-regulated businesses — built for agents, underwriters, and claims.
        </p>
        <div style={styles.ctaGroup}>
          <button onClick={onGetStarted} style={styles.ctaButton}>
            Start Free Trial <span style={styles.ctaArrow}>→</span>
          </button>
          <button onClick={onSignIn} style={styles.signInButton}>Sign In</button>
        </div>
        <p style={styles.trialNote}>10 free lookups per month for Michigan-based agencies · Business email required</p>
        <p style={styles.poweredByNote}>Provided for Michigan insurance professionals by UpFront Risk Solutions</p>
      </div>

      <div style={styles.licenseTypesStrip}>
        <p style={styles.licenseTypesLabel}>License types covered</p>
        <div style={styles.licenseTypesRow}>
          {LICENSE_TYPES.map((t) => (
            <div key={t.slug} style={styles.licenseTypeChip}>
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {t.tier !== 'live' && !t.liveLookup && <span style={styles.licenseTypeChipTag}>in progress</span>}
            </div>
          ))}
        </div>
      </div>

      <div style={styles.featuresGrid}>
        {[
          { icon: '📋', title: 'Full License Data', desc: 'Status, permits, quota info, license history' },
          { icon: '⚠️', title: 'Violation History', desc: 'Violation records with dispositions' },
          { icon: '🏢', title: 'Entity Intelligence', desc: 'Principals, registered agent, good standing' },
        ].map((f, i) => (
          <div key={i} style={styles.featureCard}>
            <div style={styles.featureIcon}>{f.icon}</div>
            <h3 style={styles.featureTitle}>{f.title}</h3>
            <p style={styles.featureDesc}>{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// REGISTER VIEW
// ============================================

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA',
  'ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK',
  'OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

function LinkedInButton() {
  return (
    <a href="/api/auth?action=linkedin_start" style={styles.linkedinButton}>
      <span style={styles.linkedinMark}>in</span> Continue with LinkedIn
    </a>
  );
}

function RegisterView({ onRegister, onBack, startWithLogin = false, onLoginRequest, loginNotice = '', registerNotice = '', prefill = null, linkedinEnabled = false }) {
  const [formData, setFormData] = useState({ firstName: prefill?.firstName || '', lastName: prefill?.lastName || '', email: '', agencyState: 'MI' });
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogin, setShowLogin] = useState(startWithLogin);
  const [loginEmail, setLoginEmail] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setError('Please enter your name and work email.');
      return;
    }
    setIsSubmitting(true);
    const result = await onRegister(formData);
    setIsSubmitting(false);
    if (result.linkSent) { setSentTo(result.email); setShowLogin(true); return; }
    if (!result.success) setError(result.error);
  };

  const handleLogin = async () => {
    setError('');
    const email = loginEmail.trim().toLowerCase();
    if (!email) { setError('Please enter your email'); return; }
    setIsSubmitting(true);
    const d = await onLoginRequest(email);
    setIsSubmitting(false);
    if (d.success) { setSentTo(email); return; }
    if (d.notFound) {
      setFormData((f) => ({ ...f, email }));
      setShowLogin(false);
      setError("We don't have an account for that email yet. Add your name below and you're in.");
      return;
    }
    setError(d.error || 'Sign-in failed. Please try again.');
  };

  if (showLogin) {
    return (
      <div style={styles.registerContainer}>
        <button onClick={() => { setShowLogin(false); setSentTo(''); }} style={styles.backButton}>← Back</button>
        <div style={styles.registerCard}>
          <h2 style={styles.registerTitle}>Welcome back</h2>
          {sentTo ? (
            <div style={styles.successBox}>
              Check your inbox. We sent a one-click sign-in link to <b>{sentTo}</b>. It works once and expires in 30 minutes.
            </div>
          ) : (
            <>
              <p style={styles.registerSubtitle}>Enter your work email and we&apos;ll send you a one-click sign-in link. No password.</p>
              {loginNotice && <div style={styles.successBox}>{loginNotice}</div>}
              {error && <div style={styles.errorBox}>{error}</div>}
              <div style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Work email</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    style={styles.input}
                    placeholder="you@youragency.com"
                    autoFocus
                  />
                </div>
                <button onClick={handleLogin} style={styles.submitButton} disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Email me a sign-in link'}
                </button>
                {linkedinEnabled && <LinkedInButton />}
              </div>
            </>
          )}
          <div style={styles.loginDivider}><span>or</span></div>
          <button onClick={() => { setShowLogin(false); setSentTo(''); }} style={styles.loginLink}>
            New to Licensr? Get started →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.registerContainer}>
      <button onClick={onBack} style={styles.backButton}>← Back</button>
      <div style={styles.registerCard}>
        <h2 style={styles.registerTitle}>Start looking up licenses</h2>
        <p style={styles.registerSubtitle}>Three fields and you&apos;re in. No password.</p>
        {registerNotice && !error && <div style={styles.successBox}>{registerNotice}</div>}
        {error && <div style={styles.errorBox}>{error}</div>}
        {linkedinEnabled && !prefill && (
          <>
            <LinkedInButton />
            <div style={styles.loginDivider}><span>or with your work email</span></div>
          </>
        )}
        <div style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>First name</label>
              <input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} style={styles.input} placeholder="John" autoFocus />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Last name</label>
              <input type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} style={styles.input} placeholder="Smith" />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Work email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              style={styles.input}
              placeholder="you@youragency.com"
            />
            <span style={styles.inputHint}>We&apos;ll pull your agency name from your email&apos;s website.</span>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Agency state</label>
            <select value={formData.agencyState} onChange={(e) => setFormData({ ...formData, agencyState: e.target.value })} style={styles.input}>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span style={styles.inputHint}>Michigan agencies get 10 free lookups a month.</span>
          </div>
          <button onClick={handleSubmit} style={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? 'Setting you up…' : 'Get started'}
          </button>
        </div>
        <div style={styles.loginDivider}><span>or</span></div>
        <button onClick={() => setShowLogin(true)} style={styles.loginLink}>
          Already have an account? Sign in →
        </button>
      </div>
    </div>
  );
}

// ============================================
// SELECT VIEW  (license-type carousel + result)
// ============================================

function SelectView({ selectedType, embed, onSelectType, onClear, quota, checkAndConsumeQuota, user, onQuotaChange }) {
  // 3D coverflow carousel: `active` is the index of the centered card.
  const [active, setActive] = useState(0);
  const [checkingLink, setCheckingLink] = useState(false);
  const [linkBlocked, setLinkBlocked] = useState(false);
  const total = LICENSE_TYPES.length;

  // If a non-liquor type is selected, show its result panel.
  if (selectedType && selectedType.slug !== 'liquor') {
    const resolved = resolveLicenseType(selectedType);
    const noQuotaLeft = quota?.loaded && quota.remaining !== null && quota.remaining <= 0;

    // Live state-record lookup (skilled trades, cannabis, adult care, child care,
    // pesticide, auto repair). Metered server-side by /api/license-lookup.
    if (selectedType.liveLookup) {
      return (
        <div style={styles.selectContainer}>
          {!embed && (
            <button onClick={onClear} style={styles.backButton}>← All license types</button>
          )}
          <div style={{ ...styles.resultCard, maxWidth: '780px' }}>
            <div style={{ ...styles.resultIcon, background: `linear-gradient(135deg, ${selectedType.accent}, ${selectedType.accent}cc)` }}>
              {selectedType.icon}
            </div>
            <div style={styles.resultBadgeRow}>
              <span style={styles.verifyBadge}>Live State Record</span>
            </div>
            <h2 style={styles.resultTitle}>{selectedType.label}</h2>
            <p style={styles.resultAgency}>{selectedType.agency}</p>
            <p style={styles.resultNote}>{selectedType.note}</p>
            {noQuotaLeft && (
              <div style={styles.limitWarning}>
                ⚠️ Monthly free lookup limit reached ({quota?.monthlyLimit ?? 10}/month). Pricing for additional lookups is coming soon — contact support@upfrontrisk.io.
              </div>
            )}
            <StateLookupPanel type={selectedType} user={user} onQuotaChange={onQuotaChange} />
            {selectedType.deepLink && (
              <p style={styles.resultRoadmap}>
                Prefer the state&apos;s own search?{' '}
                <a href={selectedType.deepLink} target="_blank" rel="noopener noreferrer" style={{ color: '#7dd3fc', textDecoration: 'none' }}>
                  Open {selectedType.agencyShort} ↗
                </a>
              </p>
            )}
          </div>
        </div>
      );
    }

    // Contractor/Auto Repair don't run a real engine yet — they just redirect to the
    // state's own site — but per product decision, that redirect still counts against
    // the same pooled 10/month free-tier quota so the promise is consistent across all
    // three license types from day one.
    const handleDeepLinkClick = async (e) => {
      e.preventDefault();
      if (!checkAndConsumeQuota) {
        window.open(selectedType.deepLink, '_blank', 'noopener,noreferrer');
        return;
      }
      setCheckingLink(true);
      const allowed = await checkAndConsumeQuota(selectedType.slug, 'deep_link_click');
      setCheckingLink(false);
      if (allowed) {
        window.open(selectedType.deepLink, '_blank', 'noopener,noreferrer');
      } else {
        setLinkBlocked(true);
      }
    };

    return (
      <div style={styles.selectContainer}>
        {!embed && (
          <button onClick={onClear} style={styles.backButton}>← All license types</button>
        )}
        <div style={styles.resultCard}>
          <div style={{ ...styles.resultIcon, background: `linear-gradient(135deg, ${selectedType.accent}, ${selectedType.accent}cc)` }}>
            {selectedType.icon}
          </div>
          <div style={styles.resultBadgeRow}>
            {resolved.status === 'verify' && (
              <span style={styles.verifyBadge}>State Verification</span>
            )}
            {resolved.status === 'cached' && (
              <span style={styles.cachedBadge}>⚙️ Cached search in development</span>
            )}
          </div>
          <h2 style={styles.resultTitle}>{selectedType.label}</h2>
          <p style={styles.resultAgency}>{selectedType.agency}</p>
          <p style={styles.resultNote}>{selectedType.note}</p>
          {(linkBlocked || noQuotaLeft) && (
            <div style={styles.limitWarning}>
              ⚠️ Monthly free lookup limit reached ({quota?.monthlyLimit ?? 10}/month). Pricing for additional lookups is coming soon — contact support@upfrontrisk.io.
            </div>
          )}
          {selectedType.deepLink && (
            <button
              onClick={handleDeepLinkClick}
              style={styles.resultCta}
              disabled={checkingLink || noQuotaLeft}
            >
              {checkingLink ? 'Checking availability…' : <>Verify with {selectedType.agencyShort} <span style={styles.ctaArrow}>↗</span></>}
            </button>
          )}
          <p style={styles.resultRoadmap}>
            {resolved.status === 'cached'
              ? 'Full Licensr intelligence for this type is on the roadmap. For now, this opens the official State of Michigan record.'
              : 'Licensr intelligence for this type is on the roadmap. For now, this opens the official State of Michigan verification search.'}
          </p>
        </div>
      </div>
    );
  }

  // Spin the wheel one step in either direction (wraps around).
  const spin = (dir) => setActive((prev) => (prev + dir + total) % total);

  // Shortest signed offset from the active card on a circular wheel,
  // so cards rotate the short way round instead of unwinding the long way.
  const offsetOf = (i) => {
    let d = i - active;
    if (d > total / 2) d -= total;
    if (d < -total / 2) d += total;
    return d;
  };

  return (
    <div style={styles.selectContainer}>
      <div style={styles.selectHeader}>
        <h1 style={styles.selectTitle}>What type of license?</h1>
        <p style={styles.selectSubtitle}>
          Choose a category to begin. Liquor runs the full Licensr intelligence engine —
          other types pull the State of Michigan&apos;s own record live.
        </p>
      </div>

      <div style={styles.carouselWrap}>
        <button style={{ ...styles.carouselArrow, left: 0 }} onClick={() => spin(-1)} aria-label="Previous license type">‹</button>

        <div style={styles.stage}>
          <div style={styles.stageInner}>
            {LICENSE_TYPES.map((t, i) => {
              const off = offsetOf(i);
              const isActive = off === 0;
              const isLive = t.tier === 'live';
              const abs = Math.abs(off);
              // Beyond two steps on either side we fold the card away.
              const hidden = abs > 2;
              const transform =
                `translateX(${off * 150}px)` +
                ` translateZ(${isActive ? 60 : -abs * 90}px)` +
                ` rotateY(${off * -38}deg)` +
                ` scale(${isActive ? 1 : 0.84})`;
              return (
                <button
                  key={t.slug}
                  onClick={() => (isActive ? onSelectType(t) : setActive(i))}
                  aria-hidden={hidden}
                  tabIndex={isActive ? 0 : -1}
                  style={{
                    ...styles.typeCard,
                    transform,
                    zIndex: 10 - abs,
                    opacity: hidden ? 0 : isActive ? 1 : 0.55,
                    pointerEvents: hidden ? 'none' : 'auto',
                    cursor: hidden ? 'default' : 'pointer',
                    borderColor: isActive
                      ? (isLive ? 'rgba(245, 158, 11, 0.65)' : `${t.accent}88`)
                      : 'rgba(248, 250, 252, 0.08)',
                    boxShadow: isActive
                      ? `0 24px 60px -20px ${t.accent}66, 0 8px 24px rgba(0,0,0,0.5)`
                      : '0 8px 20px rgba(0,0,0,0.4)',
                  }}
                >
                  <div style={{ ...styles.typeIcon, background: `linear-gradient(135deg, ${t.accent}, ${t.accent}aa)` }}>
                    {t.icon}
                  </div>
                  <div style={styles.typeBadgeRow}>
                    {t.tier === 'live' && <span style={styles.liveBadge}>● Live Intelligence</span>}
                    {t.tier !== 'live' && t.liveLookup && <span style={styles.verifyBadgeSm}>Live State Record</span>}
                    {t.tier === 'verify' && !t.liveLookup && <span style={styles.verifyBadgeSm}>State Verify</span>}
                    {t.tier === 'cached' && !t.liveLookup && <span style={styles.cachedBadgeSm}>Coming Soon</span>}
                  </div>
                  <h3 style={styles.typeName}>{t.label}</h3>
                  <p style={styles.typeTagline}>{t.tagline}</p>
                  <p style={styles.typeAgency}>{t.agencyShort}</p>
                  <span style={{ ...styles.typeArrow, opacity: isActive ? 1 : 0 }}>
                    {isLive ? 'Build report →' : 'Verify →'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <button style={{ ...styles.carouselArrow, right: 0 }} onClick={() => spin(1)} aria-label="Next license type">›</button>
      </div>

      <div style={styles.dots}>
        {LICENSE_TYPES.map((t, i) => (
          <button
            key={t.slug}
            onClick={() => setActive(i)}
            aria-label={`Go to ${t.label}`}
            style={{
              ...styles.dot,
              background: i === active ? t.accent : 'rgba(248, 250, 252, 0.2)',
              width: i === active ? '22px' : '8px',
            }}
          />
        ))}
      </div>

      <p style={styles.selectFootnote}>
        🍸 Liquor delivers violation history, entity standing & risk scoring. Other categories
        return the state&apos;s current record, with a link to the official source.
      </p>
    </div>
  );
}

function LookupView({ onStartPipeline, quota, isSuperuser, onBack }) {
  const [businessName, setBusinessName] = useState('');
  const [dba, setDba] = useState('');
  const [city, setCity] = useState('');
  const [county, setCounty] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Server-verified quota (hub-backed) rather than the old client-only localStorage count.
  // hasEntitlement === false with a loaded quota means "no free-tier plan on this account"
  // (e.g. non-Michigan agency) — still allow the attempt; the server call is authoritative
  // and returns the real reason if blocked.
  const canSearch = isSuperuser || !quota?.loaded || quota.remaining === null || quota.remaining > 0;

  const handleSubmit = async () => {
    setError('');

    if (!businessName && !dba && !licenseNumber && !businessId) {
      setError('Enter a licensee name, DBA / trade name, license number, or MLCC Business ID.');
      return;
    }

    setIsSubmitting(true);
    const allowed = await onStartPipeline({
      businessName: businessName.trim(),
      dba: dba.trim(),
      city: city.trim(),
      county: county.trim(),
      licenseNumber: licenseNumber.trim(),
      businessId: businessId.trim()
    });
    setIsSubmitting(false);
    if (allowed === false) {
      setError('Monthly free lookup limit reached. Pricing for additional lookups is coming soon — contact support@upfrontrisk.io.');
    }
  };

  return (
    <div style={styles.lookupContainer}>
      {onBack && (
        <button onClick={onBack} style={styles.backButton}>← All license types</button>
      )}
      <div style={styles.lookupHeader}>
        <h1 style={styles.lookupTitle}>Build License Intelligence Report</h1>
        <p style={styles.lookupSubtitle}>
          Official MLCC government-of-record only — we verify the license, pull live
          violation history, the liquor-liability carrier on file, spirit sales and
          entity standing, then score underwriting risk.
        </p>
        {!canSearch && (
          <div style={styles.limitWarning}>
            ⚠️ Monthly free lookup limit reached ({quota?.monthlyLimit ?? 10}/month). Pricing for additional lookups is coming soon — contact support@upfrontrisk.io.
          </div>
        )}
      </div>

      <div style={styles.lookupCard}>
        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.inputSection}>
          <h3 style={styles.inputSectionTitle}>🏛️ Licensee <span style={styles.bestTag}>BEST</span></h3>
          <p style={styles.inputSectionDesc}>Search MLCC by the licensee legal name or DBA / trade name.</p>

          <div style={styles.formGroup}>
            <label style={styles.label}>Licensee Legal Name</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              style={styles.input}
              placeholder="AMC Detroit, Inc."
              disabled={!canSearch}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>DBA / Trade Name <span style={styles.optionalTag}>optional</span></label>
            <input
              type="text"
              value={dba}
              onChange={(e) => setDba(e.target.value)}
              style={styles.input}
              placeholder="Buffalo Wild Wings Grill and Bar"
              disabled={!canSearch}
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>City <span style={styles.optionalTag}>narrows results</span></label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                style={styles.input}
                placeholder="Detroit"
                disabled={!canSearch}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>County <span style={styles.optionalTag}>narrows results</span></label>
              <input
                type="text"
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                style={styles.input}
                placeholder="Wayne"
                disabled={!canSearch}
              />
            </div>
          </div>
        </div>

        <div style={styles.inputDivider}>
          <span>or look up directly by</span>
        </div>

        <div style={styles.inputSection}>
          <h3 style={styles.inputSectionTitle}>🔢 License # or Business ID</h3>
          <p style={styles.inputSectionDesc}>The most precise way to pin a single licensee.</p>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>License Number</label>
              <div style={styles.licenseInputGroup}>
                <div style={styles.stateTag}>MI</div>
                <input
                  type="text"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  style={styles.licenseInput}
                  placeholder="L-000220652"
                  disabled={!canSearch}
                />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>MLCC Business ID</label>
              <input
                type="text"
                value={businessId}
                onChange={(e) => setBusinessId(e.target.value)}
                style={styles.input}
                placeholder="228293"
                disabled={!canSearch}
              />
            </div>
          </div>
        </div>

        <div style={styles.coverageScopeNote}>
          ℹ️ This search covers active, escrow, conditional, and suspended licenses
          only; fully revoked or expired licenses are not indexed here.
        </div>

        <button
          onClick={handleSubmit}
          style={styles.buildReportButton}
          disabled={!canSearch || isSubmitting}
        >
          {isSubmitting ? 'Checking availability…' : '🚀 Build Intelligence Report'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// PIPELINE VIEW (Progress Display)
// ============================================

function PipelineView({ inputs, user, stages, setStages, results, setResults, onComplete, onCancel, onQuotaExceeded }) {
  const [error, setError] = useState(null);
  // Human-in-the-loop candidate picker (shown when the live pull returns >1 match)
  const [candidates, setCandidates] = useState(null);
  const [cacheLicense, setCacheLicense] = useState(null);

  // Run pipeline sequentially (once, on mount)
  useEffect(() => {
    runPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the live official-record request body from whatever the user gave us.
  // Identity for the server-side quota check comes from the session cookie —
  // /api/violations is the single point of consumption for the "liquor" quota.
  const liveBody = () => ({
    businessName: inputs.businessName,
    licenseeName: inputs.businessName,
    dba: inputs.dba,
    businessId: inputs.businessId,
    licenseNumber: inputs.licenseNumber,
    city: inputs.city,
    county: inputs.county,
  });

  // Finalize once a single official-record candidate is settled.
  const finalize = async (chosen, license) => {
    const violationsData = chosen.violations || [];
    const riskAnalysis = calculateRiskAnalysis(license || chosen, chosen, violationsData);
    const finalResults = {
      license: license || chosen,   // cache-tier record if we have it, else the live record
      official: chosen,             // full live official record (the source of truth)
      violations: violationsData,
      insurance: chosen.insurance || [],
      sales_history: chosen.sales_history || [],
      subordinates: chosen.subordinates || [],
      coverage_scope_note: chosen.coverage_scope_note || COVERAGE_SCOPE_NOTE,
      risk_analysis: riskAnalysis,
    };
    await new Promise(r => setTimeout(r, 400));
    onComplete(finalResults);
  };

  // Called from the candidate picker when the underwriter chooses a match.
  const chooseCandidate = (cand) => {
    setCandidates(null);
    setResults(r => ({ ...r, official: cand, violations: cand.violations || [] }));
    setStages(s => ({ ...s, official: 'complete', risk: 'running' }));
    finalize(cand, cacheLicense);
  };

  const runPipeline = async () => {
    let cacheLicenseData = null;

    try {
      // ===== STAGE 1: MLCC License Database (weekly cache tier) =====
      // Fast status/type/address from Supabase. Best-effort — the live pull is
      // the authoritative source, so a cache miss does not stop the pipeline.
      setStages(s => ({ ...s, mlcc: 'running' }));
      try {
        const searchBody = inputs.licenseNumber
          ? { licenseNumber: inputs.licenseNumber }
          : { businessName: inputs.businessName || inputs.dba };
        const searchRes = await fetch('/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(searchBody)
        });
        const searchJson = await searchRes.json();
        if (searchJson.success && searchJson.results && searchJson.results.length > 0) {
          cacheLicenseData = searchJson.results[0];
          setCacheLicense(cacheLicenseData);
          setResults(r => ({ ...r, license: cacheLicenseData, allLicenses: searchJson.results }));
          setStages(s => ({ ...s, mlcc: 'complete' }));
        } else {
          // Cache miss is fine — the live pull will resolve the licensee.
          setStages(s => ({ ...s, mlcc: 'skipped' }));
        }
      } catch (e) {
        console.error('Cache search error:', e);
        setStages(s => ({ ...s, mlcc: 'skipped' }));
      }

      // ===== STAGE 2: Live Official Record (MLCC AIMS via Apify) =====
      setStages(s => ({ ...s, official: 'running' }));
      let live;
      try {
        const res = await fetch('/api/violations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(liveBody())
        });
        live = await res.json();
      } catch (e) {
        console.error('Live official-record error:', e);
        setStages(s => ({ ...s, official: 'error' }));
        setError('The live MLCC official-record lookup failed. Please try again.');
        return;
      }

      if (live && live.status === 'auth_required') {
        setStages(s => ({ ...s, official: 'error' }));
        setError('Your session ended. Please sign in again.');
        return;
      }

      if (live && live.source === 'quota_exceeded') {
        setStages(s => ({ ...s, official: 'error' }));
        setError(live.message || 'Monthly free lookup limit reached.');
        if (onQuotaExceeded) onQuotaExceeded();
        return;
      }

      const cands = (live && live.candidates) || [];

      // No record found is a clean result, NOT an error.
      if (live && live.success && cands.length === 0) {
        setStages(s => ({ ...s, official: 'complete', risk: 'skipped' }));
        const noRecord = {
          no_record: true,
          coverage_scope_note: (live && live.coverage_scope_note) || COVERAGE_SCOPE_NOTE,
          message: (live && live.message) ||
            'No matching MLCC licensee found in active, escrow, conditional, or suspended status.',
        };
        await new Promise(r => setTimeout(r, 400));
        onComplete({
          license: cacheLicenseData,
          official: null,
          violations: [],
          insurance: [],
          sales_history: [],
          subordinates: [],
          no_record: true,
          coverage_scope_note: noRecord.coverage_scope_note,
          no_record_message: noRecord.message,
          risk_analysis: null,
        });
        return;
      }

      if (!live || !live.success) {
        setStages(s => ({ ...s, official: 'error' }));
        setError((live && live.message) || 'The live official-record lookup failed. Please try again.');
        return;
      }

      // ===== Human-in-the-loop: more than one candidate =====
      if (cands.length > 1) {
        setCandidates(cands);
        setStages(s => ({ ...s, official: 'complete' }));
        // Pipeline pauses here until the underwriter picks a candidate.
        return;
      }

      // Exactly one candidate — settle it.
      setResults(r => ({ ...r, official: cands[0], violations: cands[0].violations || [] }));
      setStages(s => ({ ...s, official: 'complete', risk: 'running' }));
      await finalize(cands[0], cacheLicenseData);

    } catch (e) {
      console.error('Pipeline error:', e);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div style={styles.pipelineContainer}>
      <div style={styles.pipelineCard}>
        <h2 style={styles.pipelineTitle}>🔍 Building License Intelligence Report</h2>
        
        <div style={styles.pipelineStages}>
          {PIPELINE_STAGES.map((stage, index) => {
            const status = stages[stage.id] || 'pending';
            const isActive = status === 'running';
            const isComplete = status === 'complete';
            const isError = status === 'error';
            const isSkipped = status === 'skipped';
            
            return (
              <div key={stage.id} style={styles.pipelineStage}>
                <div style={styles.stageConnector}>
                  <div style={{
                    ...styles.stageIcon,
                    ...(isActive ? styles.stageIconActive : {}),
                    ...(isComplete ? styles.stageIconComplete : {}),
                    ...(isError ? styles.stageIconError : {}),
                    ...(isSkipped ? styles.stageIconSkipped : {})
                  }}>
                    {isComplete ? '✓' : isError ? '✗' : isSkipped ? '–' : stage.icon}
                  </div>
                  {index < PIPELINE_STAGES.length - 1 && (
                    <div style={{
                      ...styles.stageLine,
                      ...(isComplete || isSkipped ? styles.stageLineComplete : {})
                    }} />
                  )}
                </div>
                
                <div style={styles.stageContent}>
                  <div style={styles.stageName}>{stage.name}</div>
                  <div style={styles.stageDesc}>{stage.description}</div>
                  
                  {/* Result preview */}
                  {isComplete && (
                    <div style={styles.stageResult}>
                      {stage.id === 'mlcc' && results.license && (
                        <span>→ #{results.license.license_number || results.license.business_id} · {results.license.status}</span>
                      )}
                      {stage.id === 'official' && results.official && (
                        <span>→ {results.official.violation_count || 0} violation(s) · carrier: {results.official.insurance_carrier || 'none on file'}</span>
                      )}
                      {stage.id === 'risk' && results.official && (
                        <span>→ official record settled</span>
                      )}
                    </div>
                  )}
                  
                  {isActive && (
                    <div style={styles.stageLoading}>
                      <span style={styles.loadingDot}>●</span>
                      <span style={styles.loadingDot}>●</span>
                      <span style={styles.loadingDot}>●</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Human-in-the-loop candidate picker */}
        {candidates && candidates.length > 1 && (
          <div style={styles.candidatePicker}>
            <h3 style={styles.candidateTitle}>Multiple matches found</h3>
            <p style={styles.candidateSub}>
              The MLCC record returned {candidates.length} licensees. Pick the one you&apos;re underwriting.
            </p>
            <div style={styles.candidateList}>
              {candidates.map((c, i) => (
                <button
                  key={c.business_id || c.license_number || i}
                  style={styles.candidateCard}
                  onClick={() => chooseCandidate(c)}
                >
                  <div style={styles.candidateMain}>
                    <span style={styles.candidateName}>{c.dba || c.licensee_name || 'Unknown'}</span>
                    <span style={{
                      ...styles.candidateStatus,
                      color: /active/i.test(c.status || '') ? '#10b981'
                        : /suspend/i.test(c.status || '') ? '#f87171' : '#f59e0b'
                    }}>{c.status || '—'}</span>
                  </div>
                  <div style={styles.candidateMeta}>
                    {c.licensee_name && c.dba && <span>{c.licensee_name}</span>}
                    {c.address && <span>{c.address}</span>}
                    <span>
                      {c.county ? `${c.county} County · ` : ''}
                      Biz ID {c.business_id || '—'}
                      {c.license_number ? ` · ${c.license_number}` : ''}
                      {` · ${c.violation_count || 0} violation(s)`}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <div style={styles.coverageScopeNoteSmall}>ℹ️ {COVERAGE_SCOPE_NOTE}</div>
            <button onClick={onCancel} style={styles.cancelButtonSmall}>Cancel</button>
          </div>
        )}

        {error && (
          <div style={styles.pipelineError}>
            <p>{error}</p>
            <button onClick={onCancel} style={styles.cancelButton}>← Try Again</button>
          </div>
        )}

        {!error && !candidates && (
          <button onClick={onCancel} style={styles.cancelButtonSmall}>Cancel</button>
        )}
      </div>
    </div>
  );
}

// ============================================
// REPORT VIEW (Final Results)
// ============================================

function ReportView({ data, activeTab, setActiveTab, onNewSearch }) {
  const { license, official, violations, risk_analysis } = data;
  const insurance = data.insurance || official?.insurance || [];
  const salesHistory = data.sales_history || official?.sales_history || [];
  const subordinates = data.subordinates || official?.subordinates || [];
  const coverageNote = data.coverage_scope_note || official?.coverage_scope_note || COVERAGE_SCOPE_NOTE;
  const reportCardRef = useRef(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const businessName =
    official?.dba || official?.licensee_name ||
    license?.dba || license?.licensed_premises?.dba || license?.business_name || 'Business';

  const exportToPdf = async () => {
    if (!reportCardRef.current) return;
    setIsPdfGenerating(true);
    try {
      const element = reportCardRef.current;
      const canvas = await html2canvas(element, {
        useCORS: true,
        backgroundColor: '#0f172a',
        scale: 2,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      // A4 in points: 595 x 842, but we use pixel dimensions scaled
      const pdfWidth = 595.28;
      const pdfPageHeight = 841.89;
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      const pdf = new jsPDF({
        orientation: scaledHeight > pdfWidth ? 'portrait' : 'landscape',
        unit: 'pt',
        format: [pdfWidth, Math.min(scaledHeight, pdfPageHeight * 10)],
      });
      let yOffset = 0;
      const pageHeight = pdfWidth * (pdfPageHeight / pdfWidth);
      while (yOffset < scaledHeight) {
        if (yOffset > 0) pdf.addPage([pdfWidth, Math.min(scaledHeight - yOffset, pageHeight)]);
        pdf.addImage(
          imgData,
          'PNG',
          0,
          -yOffset,
          pdfWidth,
          scaledHeight
        );
        yOffset += pageHeight;
      }
      const safeName = businessName.replace(/[^a-z0-9_-]/gi, '-').replace(/-+/g, '-').slice(0, 60);
      pdf.save(`Licensr-Report-${safeName}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'violations', label: `Violations (${violations?.length || 0})` },
    { id: 'insurance', label: 'Insurance & Sales' },
    { id: 'entity', label: 'Entity' },
    { id: 'risk', label: 'Risk Analysis' },
    { id: 'summary', label: 'License Summary' }
  ];

  // No-record outcome is a clean result, not a failure.
  if (data.no_record) {
    return (
      <div style={styles.reportContainer}>
        <div style={styles.reportHeader}>
          <button onClick={onNewSearch} style={styles.newSearchButton}>← New Search</button>
        </div>
        <div style={styles.reportCard}>
          <div style={styles.noRecordCard}>
            <span style={styles.noRecordIcon}>🔍</span>
            <h2 style={styles.noRecordTitle}>No matching MLCC record</h2>
            <p style={styles.noRecordText}>
              {data.no_record_message ||
                'No matching MLCC licensee found in active, escrow, conditional, or suspended status.'}
            </p>
            <div style={styles.coverageScopeNote}>ℹ️ {coverageNote}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.reportContainer}>
      <div style={styles.reportHeader}>
        <button onClick={onNewSearch} style={styles.newSearchButton}>← New Search</button>
        <button onClick={exportToPdf} style={styles.pdfButton} disabled={isPdfGenerating}>
          {isPdfGenerating ? 'Generating...' : '⬇ Download PDF'}
        </button>
      </div>

      <div style={styles.reportCard} ref={reportCardRef}>
        {/* Header */}
        <div style={styles.reportCardHeader}>
          <div style={styles.reportIcon}>🍸</div>
          <div style={styles.reportTitleSection}>
            <h2 style={styles.reportTitle}>
              {decodeHtmlEntities(official?.dba || official?.licensee_name || license?.dba || license?.business_name || 'Business')}
            </h2>
            <p style={styles.reportSubtitle}>
              {decodeHtmlEntities(official?.dba && official?.licensee_name ? official.licensee_name : (license?.business_name || ''))}
            </p>
            <p style={styles.reportAddress}>
              {decodeHtmlEntities(official?.address || license?.address || '')}
              {(official?.county || license?.county) ? ` · ${official?.county || license?.county} County, MI` : ''}
            </p>
          </div>
          <RiskBadge tier={risk_analysis?.risk_tier} score={risk_analysis?.overall_risk_score} />
        </div>

        {/* Tabs */}
        <div style={styles.tabBar}>
          {tabs.map(tab => (
            <button 
              key={tab.id} 
              style={activeTab === tab.id ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Always-on coverage-scope disclaimer */}
        <div style={styles.coverageScopeNoteSmall}>ℹ️ {coverageNote}</div>

        {/* Tab Content */}
        <div style={activeTab === 'summary' ? styles.tabContentExpanded : styles.tabContent}>
          {activeTab === 'overview' && <OverviewTab official={official} license={license} coverageNote={coverageNote} />}
          {activeTab === 'violations' && <ViolationsTab violations={violations} />}
          {activeTab === 'insurance' && <InsuranceTab insurance={insurance} salesHistory={salesHistory} official={official} />}
          {activeTab === 'entity' && <EntityTab subordinates={subordinates} official={official} />}
          {activeTab === 'risk' && <RiskTab risk={risk_analysis} />}
          {activeTab === 'summary' && (
            <SummaryTab
              official={official}
              license={license}
              coverageNote={coverageNote}
              violations={violations}
              insurance={insurance}
              salesHistory={salesHistory}
              subordinates={subordinates}
              risk={risk_analysis}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function RiskBadge({ tier, score }) {
  const colors = { 
    low: '#059669', 
    moderate: '#f59e0b', 
    elevated: '#f97316', 
    high: '#dc2626', 
    severe: '#7f1d1d' 
  };
  return (
    <div style={{...styles.riskBadge, backgroundColor: colors[tier] || '#64748b'}}>
      <span style={styles.riskScore}>{score}</span>
      <span style={styles.riskLabel}>{tier?.toUpperCase()}</span>
    </div>
  );
}

// ============================================
// TAB COMPONENTS
// ============================================

function OverviewTab({ official, license, coverageNote }) {
  // Official record is the source of truth; cache-tier license fills gaps only.
  const o = official || {};
  const licenseNumber = o.license_number || license?.license_number;
  const status = o.status || license?.status;
  const licenseType = o.license_type || license?.license_type?.description || license?.license_type;
  const licenseGroup = o.license_group || license?.license_group;
  const county = o.county || license?.county;
  const lgu = o.lgu;
  const issueDate = o.issue_date;
  const expirationDate = o.expiration_date;
  const phone = o.phone || license?.phone;
  const licenses = o.licenses || [];
  const permits = o.permits || [];

  return (
    <div style={styles.tabPane}>
      {/* License Info */}
      <Section title="License Information">
        <DataGrid>
          <DataItem label="License #" value={licenseNumber} />
          <DataItem label="Status" value={status} />
          <DataItem label="Type" value={licenseType} />
          <DataItem label="Group" value={licenseGroup} />
          <DataItem label="County" value={county} />
          <DataItem label="Local Gov Unit" value={lgu} />
          <DataItem label="Issued" value={issueDate} />
          <DataItem label="Expires" value={expirationDate} />
        </DataGrid>
      </Section>

      {/* All licenses on the business record */}
      {licenses.length > 0 && (
        <Section title={`Licenses on Record (${licenses.length})`}>
          <div style={styles.recordList}>
            {licenses.map((l, i) => (
              <div key={l.license_number || i} style={styles.recordRow}>
                <span style={styles.recordPrimary}>{l.license_number || '—'}</span>
                <span style={styles.recordSecondary}>
                  {[l.group, l.type, l.subtype].filter(Boolean).join(' · ') || 'License'}
                </span>
                <span style={styles.recordStatus}>{l.status || '—'}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Permits */}
      {permits.length > 0 && (
        <Section title={`Permits (${permits.length})`}>
          <div style={styles.recordList}>
            {permits.map((p, i) => (
              <div key={p.permit_number || i} style={styles.recordRow}>
                <span style={styles.recordPrimary}>{p.permit_number || '—'}</span>
                <span style={styles.recordSecondary}>{p.type || 'Permit'}</span>
                <span style={styles.recordStatus}>{p.status || '—'}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Contact (official record only) */}
      {phone && (
        <Section title="Contact">
          <DataGrid>
            <DataItem label="Phone" value={phone} />
          </DataGrid>
        </Section>
      )}

      <div style={styles.coverageScopeNote}>ℹ️ {coverageNote || COVERAGE_SCOPE_NOTE}</div>
      <p style={styles.officialOnlyNote}>
        Licensr reports official MLCC record facts only. Operational data — photos, reviews,
        social signals — is provided downstream in UpWritr.
      </p>
    </div>
  );
}

function ViolationsTab({ violations }) {
  if (!violations || violations.length === 0) {
    return (
      <div style={styles.tabPane}>
        <div style={styles.cleanRecord}>
          <span style={styles.cleanIcon}>✓</span>
          <h4>Clean Record</h4>
          <p>No violations on the official MLCC record</p>
        </div>
      </div>
    );
  }

  const openCount = violations.filter(
    v => v.status && !/(closed|resolved|paid|dismissed)/i.test(v.status)
  ).length;

  return (
    <div style={styles.tabPane}>
      <Section title="Violation Summary">
        <div style={styles.violationSummary}>
          <div style={styles.summaryCard}>
            <span style={styles.summaryNumber}>{violations.length}</span>
            <span style={styles.summaryLabel}>Total Violations</span>
          </div>
          <div style={styles.summaryCard}>
            <span style={styles.summaryNumber}>{openCount}</span>
            <span style={styles.summaryLabel}>Open / Unresolved</span>
          </div>
        </div>
      </Section>

      <Section title="Violation History">
        <div style={styles.violationsList}>
          {violations.map((v, i) => {
            const isOpen = v.status && !/(closed|resolved|paid|dismissed)/i.test(v.status);
            return (
              <div key={v.violation_number || i} style={styles.violationRow}>
                <div style={styles.violationDate}>{v.date || '—'}</div>
                <div style={styles.violationDetails}>
                  <div style={styles.violationType}>
                    {v.violation_number ? `${v.violation_number}` : 'Violation'}
                  </div>
                  <div style={styles.violationDesc}>{v.description || v.disposition || '—'}</div>
                  {v.disposition && v.description && (
                    <div style={styles.violationDisposition}>{v.disposition}</div>
                  )}
                </div>
                <div style={styles.violationOutcome}>
                  <span style={{
                    ...styles.violationStatusTag,
                    backgroundColor: isOpen ? 'rgba(245, 158, 11, 0.18)' : 'rgba(100, 116, 139, 0.18)',
                    color: isOpen ? '#fbbf24' : '#94a3b8'
                  }}>
                    {v.status || '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

function InsuranceTab({ insurance, salesHistory }) {
  const ins = insurance || [];
  const sales = salesHistory || [];
  const hasNothing = ins.length === 0 && sales.length === 0;

  if (hasNothing) {
    return (
      <div style={styles.tabPane}>
        <div style={styles.noData}>
          <p>No liquor-liability carrier or spirit-sales history on the MLCC record for this licensee.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.tabPane}>
      {/* Insurance on file */}
      <Section title="Liquor-Liability Insurance on File">
        {ins.length === 0 ? (
          <div style={styles.noData}><p>No carrier listed on the official record.</p></div>
        ) : (
          <div style={styles.recordList}>
            {ins.map((p, i) => (
              <div key={i} style={styles.insuranceRow}>
                <div style={styles.insuranceMain}>
                  <span style={styles.insuranceCarrier}>{p.carrier || 'Unknown carrier'}</span>
                  <span style={styles.insuranceType}>{p.type || p.policy_name || 'Liquor Liability'}</span>
                </div>
                <div style={styles.insuranceMeta}>
                  {p.effective_date && <span>Effective {p.effective_date}</span>}
                  <span style={{
                    ...styles.violationStatusTag,
                    backgroundColor: /active/i.test(p.status || '') ? 'rgba(5, 150, 105, 0.18)' : 'rgba(100, 116, 139, 0.18)',
                    color: /active/i.test(p.status || '') ? '#34d399' : '#94a3b8'
                  }}>{p.status || '—'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Spirit sales history */}
      {sales.length > 0 && (
        <Section title={`Spirit Sales History (${sales.length} period${sales.length === 1 ? '' : 's'})`}>
          <div style={styles.salesTableWrap}>
            <table style={styles.salesTable}>
              <thead>
                <tr>
                  <th style={styles.salesTh}>Year</th>
                  <th style={styles.salesTh}>Month</th>
                  <th style={styles.salesTh}>License</th>
                  <th style={{ ...styles.salesTh, textAlign: 'right' }}>Sales</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((s, i) => (
                  <tr key={i}>
                    <td style={styles.salesTd}>{s.year || '—'}</td>
                    <td style={styles.salesTd}>{s.month || '—'}</td>
                    <td style={styles.salesTd}>{s.license || '—'}</td>
                    <td style={{ ...styles.salesTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {s.sales_amount || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function EntityTab({ subordinates, official }) {
  const subs = subordinates || [];

  return (
    <div style={styles.tabPane}>
      <Section title="Licensee Entity">
        <DataGrid>
          <DataItem label="Licensee Name" value={official?.licensee_name} />
          <DataItem label="DBA" value={official?.dba} />
          <DataItem label="Business ID" value={official?.business_id} />
          <DataItem label="County" value={official?.county} />
        </DataGrid>
      </Section>

      {subs.length > 0 ? (
        <Section title={`Related Parties (${subs.length})`}>
          <div style={styles.officersList}>
            {subs.map((s, i) => (
              <div key={i} style={styles.officerItem}>
                <span style={styles.officerName}>{s.name || '—'}</span>
                <span style={styles.officerTitle}>
                  {[s.relationship, s.status].filter(Boolean).join(' · ') || '—'}
                </span>
              </div>
            ))}
          </div>
        </Section>
      ) : (
        <div style={styles.noData}>
          <p>No related parties (stockholders, members, or subordinate entities) listed on the MLCC record.</p>
        </div>
      )}
    </div>
  );
}

function RiskTab({ risk }) {
  return (
    <div style={styles.tabPane}>
      <Section title="Overall Risk Assessment">
        <div style={styles.riskOverview}>
          <div style={styles.riskMeterContainer}>
            <div style={styles.riskMeter}>
              <div style={{
                ...styles.riskMeterFill, 
                width: `${risk?.overall_risk_score || 0}%`,
                background: risk?.overall_risk_score <= 25 ? '#059669' : 
                           risk?.overall_risk_score <= 45 ? '#f59e0b' : 
                           risk?.overall_risk_score <= 65 ? '#f97316' : '#dc2626'
              }} />
            </div>
            <div style={styles.riskMeterLabels}>
              <span>Low</span><span>Moderate</span><span>Elevated</span><span>High</span>
            </div>
          </div>
          <div style={styles.riskScoreDisplay}>
            <span style={styles.riskScoreBig}>{risk?.overall_risk_score || 0}</span>
            <span style={styles.riskTierLabel}>{risk?.risk_tier?.toUpperCase() || 'N/A'}</span>
          </div>
        </div>
      </Section>

      <Section title="Risk Factors">
        <div style={styles.factorsList}>
          {risk?.risk_factors?.map((f, i) => (
            <div key={i} style={{
              ...styles.factorItem,
              borderLeftColor: f.impact === 'positive' ? '#059669' : 
                              f.impact === 'severe' ? '#dc2626' : '#f97316'
            }}>
              <div style={styles.factorHeader}>
                <span style={styles.factorName}>{f.factor}</span>
                <span style={{
                  ...styles.factorImpact,
                  color: f.impact === 'positive' ? '#059669' : '#dc2626'
                }}>
                  {f.score_impact > 0 ? '+' : ''}{f.score_impact}
                </span>
              </div>
              <div style={styles.factorDesc}>{f.description}</div>
              {f.source && <div style={styles.factorSource}>{f.source}</div>}
            </div>
          ))}
        </div>
      </Section>

      {risk?.underwriting_flags?.length > 0 && (
        <Section title="⚠️ Underwriting Flags">
          <div style={styles.flagsList}>
            {risk.underwriting_flags.map((f, i) => (
              <div key={i} style={{
                ...styles.flagItem,
                borderColor: f.severity === 'critical' ? '#dc2626' : '#f59e0b'
              }}>
                <span style={{
                  ...styles.flagSeverity,
                  backgroundColor: f.severity === 'critical' ? '#dc2626' : '#f59e0b'
                }}>
                  {f.severity.toUpperCase()}
                </span>
                <div style={styles.flagDesc}>{f.description}</div>
                <div style={styles.flagRec}>💡 {f.recommendation}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// One-page digest of every other tab, in order — Overview, Violations,
// Insurance & Sales, Entity, Risk Analysis. This is the tab meant to become
// the PDF export surface, so a reviewer never has to click through five tabs
// to get the full picture; it's all here, top to bottom.
function SummaryTab({ official, license, coverageNote, violations, insurance, salesHistory, subordinates, risk }) {
  return (
    <div style={styles.tabPane}>
      <div style={styles.summarySectionFirst}>
        <h3 style={styles.summarySectionTitle}>Overview</h3>
        <OverviewTab official={official} license={license} coverageNote={coverageNote} />
      </div>
      <div style={styles.summarySection}>
        <h3 style={styles.summarySectionTitle}>Violations</h3>
        <ViolationsTab violations={violations} />
      </div>
      <div style={styles.summarySection}>
        <h3 style={styles.summarySectionTitle}>Insurance & Sales</h3>
        <InsuranceTab insurance={insurance} salesHistory={salesHistory} official={official} />
      </div>
      <div style={styles.summarySection}>
        <h3 style={styles.summarySectionTitle}>Entity</h3>
        <EntityTab subordinates={subordinates} official={official} />
      </div>
      <div style={styles.summarySection}>
        <h3 style={styles.summarySectionTitle}>Risk Analysis</h3>
        <RiskTab risk={risk} />
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h4 style={styles.sectionTitle}>{title}</h4>
      {children}
    </div>
  );
}

function DataGrid({ children }) {
  return <div style={styles.dataGrid}>{children}</div>;
}

function DataItem({ label, value, link }) {
  if (!value) return null;
  return (
    <div style={styles.dataItem}>
      <span style={styles.dataLabel}>{label}</span>
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" style={styles.dataLink}>{value}</a>
      ) : (
        <span style={styles.dataValue}>{value}</span>
      )}
    </div>
  );
}

// ============================================
// STYLES
// ============================================

const styles = {
  // Layout
  container: { minHeight: '100vh', backgroundColor: '#0f172a', color: '#f8fafc', fontFamily: "'Inter', -apple-system, sans-serif", position: 'relative' },
  bgPattern: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `radial-gradient(circle at 20% 50%, rgba(245, 158, 11, 0.08) 0%, transparent 50%)`, pointerEvents: 'none', zIndex: 0 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 32px', borderBottom: '1px solid rgba(248, 250, 252, 0.1)', position: 'relative', zIndex: 10, backgroundColor: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(10px)' },
  logoContainer: { display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' },
  logoIcon: { fontSize: '28px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '10px', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoText: { display: 'flex', flexDirection: 'column' },
  logoMain: { fontSize: '22px', fontWeight: 700 },
  logoAccent: { color: '#f59e0b' },
  logoSub: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  stateBadge: { background: 'rgba(99, 102, 241, 0.2)', color: '#a5b4fc', padding: '6px 12px', borderRadius: '6px', fontSize: '13px' },
  superuserBadge: { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#0f172a', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 },
  lookupCounter: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  counterLabel: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase' },
  counterValue: { fontSize: '18px', fontWeight: 600, color: '#f59e0b' },
  logoutBtn: { background: 'transparent', border: '1px solid rgba(248, 250, 252, 0.2)', color: '#94a3b8', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  main: { position: 'relative', zIndex: 10, minHeight: 'calc(100vh - 140px)' },
  footer: { textAlign: 'center', padding: '24px', borderTop: '1px solid rgba(248, 250, 252, 0.08)', color: '#64748b', fontSize: '12px', position: 'relative', zIndex: 10 },

  // Landing
  landingContainer: { maxWidth: '1200px', margin: '0 auto', padding: '60px 32px' },
  heroContent: { textAlign: 'center', marginBottom: '80px' },
  badge: { display: 'inline-block', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', padding: '8px 16px', borderRadius: '20px', fontSize: '13px', marginBottom: '24px' },
  heroTitle: { fontSize: '48px', fontWeight: 700, lineHeight: 1.1, marginBottom: '24px' },
  heroAccent: { background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSubtitle: { fontSize: '18px', color: '#94a3b8', maxWidth: '600px', margin: '0 auto 40px', lineHeight: 1.6 },
  ctaGroup: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaButton: { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#0f172a', border: 'none', padding: '16px 32px', fontSize: '16px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  signInButton: { background: 'transparent', border: '1px solid rgba(248, 250, 252, 0.3)', color: '#f8fafc', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', cursor: 'pointer' },
  ctaArrow: { fontSize: '18px' },
  trialNote: { marginTop: '16px', fontSize: '13px', color: '#64748b' },
  poweredByNote: { marginTop: '10px', fontSize: '13px', color: '#94a3b8' },
  licenseTypesStrip: { maxWidth: '900px', margin: '0 auto 40px', textAlign: 'center' },
  licenseTypesLabel: { fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' },
  licenseTypesRow: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' },
  licenseTypeChip: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(248, 250, 252, 0.04)', border: '1px solid rgba(248, 250, 252, 0.1)', borderRadius: '20px', padding: '8px 14px', fontSize: '13px', color: '#e2e8f0' },
  licenseTypeChipTag: { fontSize: '10px', color: '#f59e0b', background: 'rgba(245, 158, 11, 0.12)', borderRadius: '10px', padding: '2px 8px', marginLeft: '2px' },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' },
  featureCard: { background: 'rgba(248, 250, 252, 0.03)', border: '1px solid rgba(248, 250, 252, 0.08)', borderRadius: '12px', padding: '28px', textAlign: 'center' },
  featureIcon: { fontSize: '28px', marginBottom: '12px' },
  featureTitle: { fontSize: '16px', fontWeight: 600, marginBottom: '6px' },
  featureDesc: { fontSize: '13px', color: '#94a3b8' },

  // Register
  registerContainer: { maxWidth: '480px', margin: '0 auto', padding: '40px 32px' },
  backButton: { background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '14px', cursor: 'pointer', marginBottom: '24px', padding: 0 },
  registerCard: { background: 'rgba(248, 250, 252, 0.03)', border: '1px solid rgba(248, 250, 252, 0.08)', borderRadius: '16px', padding: '40px' },
  registerTitle: { fontSize: '28px', fontWeight: 700, marginBottom: '8px' },
  registerSubtitle: { color: '#94a3b8', marginBottom: '32px' },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 },
  label: { fontSize: '13px', fontWeight: 500, color: '#cbd5e1' },
  input: { width: '100%', boxSizing: 'border-box', minWidth: 0, background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(248, 250, 252, 0.15)', borderRadius: '8px', padding: '12px 14px', fontSize: '15px', color: '#f8fafc', outline: 'none' },
  inputHint: { fontSize: '11px', color: '#64748b' },
  submitButton: { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#0f172a', border: 'none', padding: '14px', fontSize: '15px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', marginTop: '8px' },
  loginDivider: { display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '24px 0 16px', color: '#64748b', fontSize: '13px' },
  loginLink: { background: 'transparent', border: '1px solid rgba(248, 250, 252, 0.2)', color: '#94a3b8', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', width: '100%' },
  linkedinButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#0a66c2', color: '#ffffff', textDecoration: 'none', padding: '13px', fontSize: '15px', fontWeight: 600, borderRadius: '8px', marginTop: '8px' },
  linkedinMark: { background: '#ffffff', color: '#0a66c2', fontWeight: 800, fontSize: '13px', borderRadius: '3px', padding: '0 4px', lineHeight: '18px', fontFamily: 'Arial, sans-serif' },
  noticeBar: { maxWidth: '780px', margin: '0 auto 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.35)', color: '#bbf7d0', padding: '10px 16px', borderRadius: '8px', fontSize: '14px' },
  noticeClose: { background: 'none', border: 'none', color: '#bbf7d0', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
  successBox: { background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.35)', color: '#bbf7d0', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', lineHeight: 1.5 },
  errorBox: { background: 'rgba(220, 38, 38, 0.15)', border: '1px solid rgba(220, 38, 38, 0.3)', color: '#fca5a5', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },

  // Lookup
  lookupContainer: { maxWidth: '700px', margin: '0 auto', padding: '40px 32px' },
  lookupHeader: { textAlign: 'center', marginBottom: '32px' },
  lookupTitle: { fontSize: '28px', fontWeight: 700, marginBottom: '8px' },
  lookupSubtitle: { color: '#94a3b8', fontSize: '15px' },
  limitWarning: { background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#fbbf24', padding: '12px 20px', borderRadius: '8px', marginTop: '16px', fontSize: '14px' },
  lookupCard: { background: 'rgba(248, 250, 252, 0.03)', border: '1px solid rgba(248, 250, 252, 0.08)', borderRadius: '16px', padding: '32px' },
  inputSection: { marginBottom: '24px' },
  inputSectionTitle: { fontSize: '16px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
  inputSectionDesc: { fontSize: '13px', color: '#64748b', marginBottom: '16px' },
  bestTag: { background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 },
  optionalTag: { color: '#64748b', fontSize: '11px', fontWeight: 400 },
  inputDivider: { display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '24px 0', color: '#475569', fontSize: '12px', textTransform: 'uppercase' },
  licenseInputGroup: { display: 'flex', gap: '8px', alignItems: 'center' },
  stateTag: { background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', padding: '12px 16px', borderRadius: '8px', fontWeight: 600 },
  licenseInput: { flex: 1, background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(248, 250, 252, 0.15)', borderRadius: '8px', padding: '12px 14px', fontSize: '15px', color: '#f8fafc', outline: 'none' },
  buildReportButton: { width: '100%', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#0f172a', border: 'none', padding: '16px', fontSize: '16px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer', marginTop: '24px' },

  // Select (license-type carousel)
  selectContainer: { maxWidth: '1100px', margin: '0 auto', padding: '48px 24px' },
  selectHeader: { textAlign: 'center', marginBottom: '36px' },
  selectTitle: { fontSize: '34px', fontWeight: 700, marginBottom: '12px' },
  selectSubtitle: { color: '#94a3b8', fontSize: '16px', maxWidth: '620px', margin: '0 auto', lineHeight: 1.6 },
  carouselWrap: { position: 'relative', padding: '0 44px' },
  // 3D coverflow stage
  stage: { perspective: '1400px', height: '340px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  stageInner: { position: 'relative', width: '260px', height: '300px', transformStyle: 'preserve-3d' },
  carouselArrow: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 20, width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(15, 23, 42, 0.92)', border: '1px solid rgba(248, 250, 252, 0.18)', color: '#f8fafc', fontSize: '22px', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' },
  typeCard: { position: 'absolute', top: 0, left: 0, width: '260px', minHeight: '290px', textAlign: 'left', background: 'rgba(20, 30, 48, 0.96)', border: '1px solid rgba(248, 250, 252, 0.08)', borderRadius: '16px', padding: '22px', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '10px', transformStyle: 'preserve-3d', transition: 'transform 0.55s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.45s ease, box-shadow 0.45s ease, border-color 0.45s ease', willChange: 'transform, opacity' },
  dots: { display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '4px' },
  dot: { height: '8px', borderRadius: '20px', border: 'none', padding: 0, cursor: 'pointer', transition: 'background 0.3s ease, width 0.3s ease' },
  typeIcon: { fontSize: '24px', borderRadius: '12px', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  typeBadgeRow: { display: 'flex' },
  liveBadge: { background: 'rgba(245, 158, 11, 0.18)', color: '#f59e0b', fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px' },
  verifyBadgeSm: { background: 'rgba(56, 189, 248, 0.15)', color: '#7dd3fc', fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px' },
  cachedBadgeSm: { background: 'rgba(100, 116, 139, 0.18)', color: '#94a3b8', fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px' },
  typeName: { fontSize: '19px', fontWeight: 700, margin: 0 },
  typeTagline: { fontSize: '13px', color: '#94a3b8', margin: 0, lineHeight: 1.4, flex: 1 },
  typeAgency: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 },
  typeArrow: { fontSize: '13px', fontWeight: 600, color: '#f59e0b', marginTop: '4px' },
  selectFootnote: { textAlign: 'center', color: '#64748b', fontSize: '13px', marginTop: '20px', maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 },

  // Select result card (non-liquor)
  resultCard: { maxWidth: '520px', margin: '0 auto', background: 'rgba(248, 250, 252, 0.03)', border: '1px solid rgba(248, 250, 252, 0.08)', borderRadius: '18px', padding: '36px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' },
  resultIcon: { fontSize: '30px', borderRadius: '16px', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' },
  resultBadgeRow: { display: 'flex', justifyContent: 'center' },
  verifyBadge: { background: 'rgba(56, 189, 248, 0.15)', color: '#7dd3fc', fontSize: '12px', fontWeight: 600, padding: '6px 14px', borderRadius: '20px' },
  cachedBadge: { background: 'rgba(100, 116, 139, 0.18)', color: '#cbd5e1', fontSize: '12px', fontWeight: 600, padding: '6px 14px', borderRadius: '20px' },
  resultTitle: { fontSize: '26px', fontWeight: 700, margin: '6px 0 0' },
  resultAgency: { fontSize: '14px', color: '#94a3b8', margin: 0 },
  resultNote: { fontSize: '14px', color: '#cbd5e1', margin: '8px 0 0', lineHeight: 1.5 },
  resultCta: { display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '18px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#0f172a', textDecoration: 'none', padding: '14px 26px', fontSize: '15px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'inherit' },
  resultRoadmap: { fontSize: '12px', color: '#64748b', margin: '16px 0 0', lineHeight: 1.6, maxWidth: '400px' },

  // Pipeline
  pipelineContainer: { maxWidth: '600px', margin: '0 auto', padding: '60px 32px' },
  pipelineCard: { background: 'rgba(248, 250, 252, 0.03)', border: '1px solid rgba(248, 250, 252, 0.08)', borderRadius: '16px', padding: '40px' },
  pipelineTitle: { fontSize: '20px', fontWeight: 600, marginBottom: '32px', textAlign: 'center' },
  pipelineStages: { display: 'flex', flexDirection: 'column', gap: '0' },
  pipelineStage: { display: 'flex', gap: '16px', minHeight: '80px' },
  stageConnector: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '40px' },
  stageIcon: { width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(100, 116, 139, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#64748b', flexShrink: 0 },
  stageIconActive: { background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', animation: 'pulse 1.5s infinite' },
  stageIconComplete: { background: 'rgba(5, 150, 105, 0.2)', color: '#10b981' },
  stageIconError: { background: 'rgba(220, 38, 38, 0.2)', color: '#ef4444' },
  stageIconSkipped: { background: 'rgba(100, 116, 139, 0.1)', color: '#475569' },
  stageLine: { width: '2px', flex: 1, background: 'rgba(100, 116, 139, 0.3)', margin: '4px 0' },
  stageLineComplete: { background: 'rgba(5, 150, 105, 0.5)' },
  stageContent: { flex: 1, paddingBottom: '16px' },
  stageName: { fontSize: '15px', fontWeight: 600, marginBottom: '2px' },
  stageDesc: { fontSize: '13px', color: '#64748b' },
  stageResult: { fontSize: '12px', color: '#10b981', marginTop: '6px' },
  stageLoading: { marginTop: '6px', display: 'flex', gap: '4px' },
  loadingDot: { color: '#f59e0b', animation: 'blink 1.4s infinite', fontSize: '8px' },
  pipelineError: { marginTop: '24px', padding: '16px', background: 'rgba(220, 38, 38, 0.1)', borderRadius: '8px', textAlign: 'center' },
  cancelButton: { marginTop: '12px', background: 'transparent', border: '1px solid rgba(248, 250, 252, 0.2)', color: '#94a3b8', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' },
  cancelButtonSmall: { marginTop: '24px', background: 'transparent', border: 'none', color: '#64748b', fontSize: '13px', cursor: 'pointer', width: '100%' },

  // Report
  reportContainer: { maxWidth: '1000px', margin: '0 auto', padding: '32px' },
  reportHeader: { marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' },
  newSearchButton: { background: 'rgba(248, 250, 252, 0.05)', border: '1px solid rgba(248, 250, 252, 0.15)', color: '#94a3b8', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  pdfButton: { background: 'rgba(99, 102, 241, 0.15)', border: '1px solid rgba(99, 102, 241, 0.4)', color: '#a5b4fc', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 },
  reportCard: { background: 'rgba(248, 250, 252, 0.03)', border: '1px solid rgba(248, 250, 252, 0.08)', borderRadius: '16px', overflow: 'hidden' },
  reportCardHeader: { display: 'flex', gap: '16px', padding: '24px', borderBottom: '1px solid rgba(248, 250, 252, 0.08)', alignItems: 'flex-start' },
  reportIcon: { fontSize: '32px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '12px', width: '52px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  reportTitleSection: { flex: 1 },
  reportTitle: { fontSize: '22px', fontWeight: 700, marginBottom: '4px' },
  reportSubtitle: { fontSize: '14px', color: '#94a3b8', marginBottom: '4px' },
  reportAddress: { fontSize: '13px', color: '#64748b' },
  riskBadge: { padding: '12px 16px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white', minWidth: '70px' },
  riskScore: { fontSize: '28px', fontWeight: 700 },
  riskLabel: { fontSize: '10px', fontWeight: 600 },
  tabBar: { display: 'flex', borderBottom: '1px solid rgba(248, 250, 252, 0.08)', padding: '0 16px' },
  tab: { background: 'transparent', border: 'none', color: '#64748b', padding: '14px 16px', cursor: 'pointer', fontSize: '14px', borderBottom: '2px solid transparent' },
  tabActive: { background: 'transparent', border: 'none', color: '#f59e0b', padding: '14px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, borderBottom: '2px solid #f59e0b' },
  tabContent: { padding: '24px', maxHeight: '500px', overflowY: 'auto' },
  tabContentExpanded: { padding: '24px' },
  tabPane: { display: 'flex', flexDirection: 'column', gap: '24px' },
  summarySection: { borderTop: '1px solid rgba(248, 250, 252, 0.08)', paddingTop: '24px' },
  summarySectionFirst: {},
  summarySectionTitle: { fontSize: '13px', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' },

  // Section
  section: { marginBottom: '8px' },
  sectionTitle: { fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(248, 250, 252, 0.08)' },

  // Data
  dataGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
  dataItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  dataLabel: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase' },
  dataValue: { fontSize: '14px', color: '#f8fafc' },
  dataLink: { fontSize: '14px', color: '#60a5fa', textDecoration: 'none' },

  // Coverage-scope disclaimer (CRITICAL — always shown)
  coverageScopeNote: { background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.25)', color: '#bae6fd', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', lineHeight: 1.5 },
  coverageScopeNoteSmall: { background: 'rgba(56, 189, 248, 0.06)', borderBottom: '1px solid rgba(248, 250, 252, 0.06)', color: '#7dd3fc', padding: '10px 24px', fontSize: '12px', lineHeight: 1.5 },
  officialOnlyNote: { fontSize: '12px', color: '#64748b', lineHeight: 1.5, marginTop: '4px' },

  // Record lists (licenses / permits on record)
  recordList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  recordRow: { display: 'grid', gridTemplateColumns: '140px 1fr auto', gap: '12px', alignItems: 'center', padding: '12px 14px', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '8px' },
  recordPrimary: { fontSize: '13px', fontWeight: 600, color: '#f8fafc', fontVariantNumeric: 'tabular-nums' },
  recordSecondary: { fontSize: '13px', color: '#94a3b8' },
  recordStatus: { fontSize: '12px', color: '#cbd5e1', textTransform: 'capitalize' },

  // Insurance
  insuranceRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '8px', flexWrap: 'wrap' },
  insuranceMain: { display: 'flex', flexDirection: 'column', gap: '2px' },
  insuranceCarrier: { fontSize: '15px', fontWeight: 600, color: '#f8fafc' },
  insuranceType: { fontSize: '12px', color: '#94a3b8' },
  insuranceMeta: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#94a3b8' },

  // Sales table
  salesTableWrap: { overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(248, 250, 252, 0.08)' },
  salesTable: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  salesTh: { textAlign: 'left', padding: '10px 14px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', borderBottom: '1px solid rgba(248, 250, 252, 0.08)', background: 'rgba(15, 23, 42, 0.5)', position: 'sticky', top: 0 },
  salesTd: { padding: '10px 14px', color: '#cbd5e1', borderBottom: '1px solid rgba(248, 250, 252, 0.04)' },

  // No-record clean result
  noRecordCard: { textAlign: 'center', padding: '56px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  noRecordIcon: { fontSize: '48px' },
  noRecordTitle: { fontSize: '22px', fontWeight: 700, margin: 0 },
  noRecordText: { fontSize: '15px', color: '#94a3b8', maxWidth: '480px', lineHeight: 1.6, margin: 0 },

  // Candidate picker (human-in-the-loop)
  candidatePicker: { marginTop: '28px', paddingTop: '24px', borderTop: '1px solid rgba(248, 250, 252, 0.1)' },
  candidateTitle: { fontSize: '17px', fontWeight: 700, margin: '0 0 4px' },
  candidateSub: { fontSize: '13px', color: '#94a3b8', margin: '0 0 16px', lineHeight: 1.5 },
  candidateList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  candidateCard: { textAlign: 'left', width: '100%', background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(248, 250, 252, 0.1)', borderRadius: '10px', padding: '14px 16px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '6px', transition: 'border-color 0.2s ease, background 0.2s ease' },
  candidateMain: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  candidateName: { fontSize: '15px', fontWeight: 600, color: '#f8fafc' },
  candidateStatus: { fontSize: '12px', fontWeight: 600, textTransform: 'capitalize' },
  candidateMeta: { display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px', color: '#94a3b8' },

  // Violations
  cleanRecord: { textAlign: 'center', padding: '40px', background: 'rgba(5, 150, 105, 0.1)', borderRadius: '8px' },
  cleanIcon: { fontSize: '48px', color: '#059669' },
  violationSummary: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
  summaryCard: { background: 'rgba(15, 23, 42, 0.5)', padding: '16px', borderRadius: '8px', textAlign: 'center' },
  summaryNumber: { display: 'block', fontSize: '28px', fontWeight: 700, color: '#f59e0b' },
  summaryLabel: { fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' },
  violationsList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  violationRow: { display: 'grid', gridTemplateColumns: '100px 1fr auto', gap: '16px', padding: '16px', background: 'rgba(220, 38, 38, 0.05)', borderRadius: '8px', borderLeft: '3px solid #dc2626' },
  violationDate: { fontSize: '13px', fontWeight: 600, color: '#fca5a5' },
  violationDetails: { flex: 1 },
  violationType: { fontSize: '14px', fontWeight: 600, marginBottom: '4px' },
  violationDesc: { fontSize: '13px', color: '#94a3b8', lineHeight: 1.5 },
  violationDisposition: { fontSize: '12px', color: '#64748b', marginTop: '6px', lineHeight: 1.5 },
  violationOutcome: { textAlign: 'right' },
  violationStatusTag: { fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap', textTransform: 'capitalize' },
  penalty: { fontSize: '14px', color: '#fca5a5', fontWeight: 600 },

  // Entity
  noData: { padding: '24px', background: 'rgba(100, 116, 139, 0.1)', borderRadius: '8px', textAlign: 'center', color: '#94a3b8' },
  agentCard: { padding: '16px', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '8px' },
  agentName: { fontSize: '15px', fontWeight: 600 },
  officersList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  officerItem: { display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '6px' },
  officerName: { fontSize: '14px', fontWeight: 500 },
  officerTitle: { fontSize: '12px', color: '#f59e0b' },

  // Risk
  riskOverview: { display: 'flex', gap: '24px', alignItems: 'center' },
  riskMeterContainer: { flex: 1 },
  riskMeter: { height: '12px', background: 'rgba(15, 23, 42, 0.8)', borderRadius: '6px', overflow: 'hidden' },
  riskMeterFill: { height: '100%', borderRadius: '6px', transition: 'width 0.5s' },
  riskMeterLabels: { display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '10px', color: '#64748b' },
  riskScoreDisplay: { textAlign: 'center', minWidth: '80px' },
  riskScoreBig: { display: 'block', fontSize: '36px', fontWeight: 700, color: '#f59e0b' },
  riskTierLabel: { fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' },
  factorsList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  factorItem: { padding: '14px 16px', background: 'rgba(15, 23, 42, 0.5)', borderRadius: '8px', borderLeft: '3px solid' },
  factorHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  factorName: { fontSize: '14px', fontWeight: 600 },
  factorImpact: { fontSize: '14px', fontWeight: 600 },
  factorDesc: { fontSize: '13px', color: '#94a3b8' },
  factorSource: { fontSize: '11px', color: '#a5b4fc', marginTop: '4px' },
  flagsList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  flagItem: { padding: '16px', background: 'rgba(220, 38, 38, 0.05)', borderRadius: '8px', border: '1px solid' },
  flagSeverity: { fontSize: '10px', fontWeight: 600, padding: '4px 8px', borderRadius: '4px', color: 'white', marginRight: '8px' },
  flagDesc: { fontSize: '14px', marginTop: '8px' },
  flagRec: { fontSize: '13px', color: '#fbbf24', marginTop: '8px' }
};

// Add keyframes for animations
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes blink {
      0%, 100% { opacity: 0.2; }
      50% { opacity: 1; }
    }
  `;
  document.head.appendChild(styleSheet);
}
